import React from "react";
import { css } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { space } from "main/javascripts/styles/base/spaceStyle";
import { Cruise } from "main/javascripts/types/cruise";
import {
  fontSize,
  fontWeight,
  lineHeight,
} from "main/javascripts/styles/base/typographyStyle";
import {
  borderColor,
  textColor,
} from "main/javascripts/styles/base/colorStyle";
import { CruiseRouteTypes } from "main/javascripts/constants/CruiseRouteTypes";
import { borderRadius } from "main/javascripts/styles/base/borderStyle";
import { moreThanBreakpoint } from "main/javascripts/styles/base/responsiveStyle";

const emptyTime = "00:00";

export interface IProps {
  cruiseRoutes: Cruise.CruiseRoute[];
}

export const CruiseRouteList: React.FC<IProps> = (
  props: IProps
): React.ReactElement => {
  const { cruiseRoutes } = props;
  const { t } = useTranslation(["component"]);

  const getLastDay = (routes?: Cruise.CruiseRoute[]) => {
    if (routes?.length === 0) {
      return 0;
    }
    const lastRoute = routes[routes.length - 1];
    return lastRoute.day;
  };
  const lastDay = getLastDay(cruiseRoutes);

  const renderRouteData = (route: Cruise.CruiseRoute) => {
    // sailingの場合終日航海と見なしている
    if (route.routeType === CruiseRouteTypes.sailing) {
      return (
        <div key={route.id}>
          <div css={routeTitleStyle}>{route.title}</div>
        </div>
      );
    }
    // 滞在箇所が基準になっていると思われるため
    // arrivalTimeを先に表示
    const timeDisplay = () => {
      if (route.day === 1 && route.arrivalTime === emptyTime) {
        return t("cruise.departs", { time: route.departureTime });
      } else if (route.day === lastDay && route.departureTime === emptyTime) {
        return t("cruise.arrives", { time: route.arrivalTime });
      } else {
        return `${route.arrivalTime} - ${route.departureTime}`;
      }
    };

    return (
      <div key={route.id}>
        <div css={routeTitleStyle}>{route.title}</div>
        <div css={routeTimeStyle}>{timeDisplay()}</div>
      </div>
    );
  };

  const groupByDay = (
    routes?: Cruise.CruiseRoute[]
  ): { [key: number]: Cruise.CruiseRoute[] } => {
    if (routes?.length === 0) {
      return {};
    }
    return routes.reduce((acc, route) => {
      if (!acc[route.day]) {
        acc[route.day] = [];
      }
      acc[route.day].push(route);
      return acc;
    }, {});
  };

  const groupedRoutes = groupByDay(cruiseRoutes);

  return (
    <div css={routeItemBlockStyle}>
      {Object.entries(groupedRoutes).map(([day, routes]) => {
        return (
          <div key={day} css={routeItemStyle}>
            <div css={routeDayStyle}>{t("cruise.dayLabel", { day: day })}</div>
            <div css={routeDataListStyle}>
              {routes.map((route) => {
                return renderRouteData(route);
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const routeItemBlockStyle = css`
  padding-top: ${space.atom2x};
`;
const routeItemStyle = css`
  display: grid;
  grid-template-columns: 4rem 1fr;
  align-items: flex-start;
  gap: ${space.atom2_5x};
  padding-bottom: ${space.atom3x};

  ${moreThanBreakpoint("tablet")} {
    gap: ${space.atom3x};
  }
`;
const routeDayStyle = css`
  font-size: ${fontSize.caption};
  line-height: ${lineHeight.caption2};
  color: ${textColor.secondaryDarkColor};
  background-color: ${borderColor.primaryLightColor};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${borderRadius.normal};
`;
const routeDataListStyle = css`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${space.atom2x};
`;
const routeTitleStyle = css`
  font-size: ${fontSize.mediumHeading};
  line-height: ${lineHeight.mediumHeadline2};
  font-weight: ${fontWeight.medium};
`;
const routeTimeStyle = css`
  font-size: ${fontSize.body};
  line-height: ${lineHeight.body2};
  color: ${textColor.secondaryDarkColor};
  opacity: 0.7;
`;
